// Client-side helpers for saving and sharing photos. Downloads fetch the object as
// a blob so the browser saves it (cross-origin <a download> is ignored otherwise);
// sharing prefers the native file share sheet, then a URL share, then clipboard.

export function filenameFromUrl(url: string, fallback = "photo.jpg"): string {
  const path = url.split("?")[0] ?? url;
  const last = path.slice(path.lastIndexOf("/") + 1);
  return last || fallback;
}

/** Save a single photo to the user's device. Returns false if it couldn't be fetched. */
export async function downloadPhoto(url: string): Promise<boolean> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    triggerBlobDownload(blob, filenameFromUrl(url));
    return true;
  } catch {
    // Fall back to opening the image so the guest can save it manually.
    window.open(url, "_blank", "noopener");
    return false;
  }
}

/** Save many photos. Resolves to the number successfully downloaded. */
export async function downloadAll(urls: string[]): Promise<number> {
  let ok = 0;
  for (const url of urls) {
    // Sequential: parallel blob downloads trip browser "multiple files" blocking.
    if (await downloadPhoto(url)) ok++;
  }
  return ok;
}

/** Share one or more photos via the native share sheet, with URL/clipboard fallbacks. */
export async function sharePhotos(urls: string[], opts?: { title?: string; text?: string }): Promise<void> {
  if (urls.length === 0) return;
  // Prefer sharing the actual image files when the platform supports it.
  try {
    const files = await Promise.all(
      urls.map(async (url) => {
        const res = await fetch(url);
        const blob = await res.blob();
        return new File([blob], filenameFromUrl(url), { type: blob.type || "image/jpeg" });
      }),
    );
    if (navigator.canShare?.({ files })) {
      await navigator.share({ files, title: opts?.title, text: opts?.text });
      return;
    }
  } catch {
    // ignore and fall through to URL/clipboard fallbacks
  }
  try {
    if (navigator.share) {
      await navigator.share({ url: urls[0], title: opts?.title, text: opts?.text });
    } else {
      await navigator.clipboard.writeText(urls.join("\n"));
    }
  } catch {
    // user cancelled or sharing unavailable — nothing to do
  }
}

function triggerBlobDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
